import { LandingInput } from '../../../../components/cells/LandingInput/LandingInput';
import { Enable, $ as $E, html, rune } from 'rune-ts';
import { OnlinePopupStoreEditor } from './OnlinePopupStoreEditor';
import { Alert, AlertProps } from '../../../../../shared/components/cells/Alert/Alert';
import { checkPhoneNumberSms, isValidEmail } from '../../../../../shared/util/validation';
import { OnlinePopupStoreText } from './OnlinePopupStoreText';
import { CreatorApiS } from '../../../../../features/Creator/outbound/share';
import { LandingInputFile } from '../../../../components/cells/LandingInputFile/LandingInputFile';
import { ButtonAction } from '../../../../../shared/components/atoms/ButtonAction/ButtonAction';
import klass from './OnlinePopupStoreEditor.module.scss';
import { InputResetEvent } from '../../../../components/atoms/Input/InputEvent';

export class OnlinePopupStoreController extends Enable {
  is_creator = true;
  is_agree = false;
  inputCreator: LandingInput;
  inputDomain: LandingInput;
  inputManager: LandingInput;
  inputCompany: LandingInput;
  inputManagerEmail: LandingInput;
  inputManagerPhone: LandingInput;
  inputManagerSns: LandingInput;
  inputFile: LandingInputFile;
  submitButton: ButtonAction;

  constructor(public override view: OnlinePopupStoreEditor) {
    super(view);
    const is_mobile = !!rune.getSharedData(view)?.is_mobile;

    const texts = new OnlinePopupStoreText().getTexts(is_mobile);

    this.inputCreator = new LandingInput({
      name: 'creator',
      required: true,
      placeholder: texts.submit_form_creator,
    });

    this.inputDomain = new LandingInput({
      name: 'marppleshop_url',
      required: true,
      placeholder: texts.submit_form_domain,
      error_helper: {
        message: texts.submit_form_creator_error,
        isError: () => false,
      },
    });

    this.inputCompany = new LandingInput({
      name: 'company',
      placeholder: texts.submit_form_company,
    });

    this.inputManager = new LandingInput({
      name: 'manager_name',
      required: true,
      placeholder: texts.submit_form_manager,
    });

    this.inputManagerEmail = new LandingInput({
      name: 'manager_email',
      type: 'email',
      delete_pattern: /[^a-zA-Z0-9@._-]+/g,
      required: true,
      placeholder: texts.submit_form_manager_email,
      error_helper: {
        message: texts.submit_form_email_error,
        isError: (value) => !isValidEmail(value),
      },
    });

    this.inputManagerPhone = new LandingInput({
      name: 'manager_phone',
      type: 'tel',
      maxlength: 11,
      delete_pattern: /\D/g,
      required: true,
      placeholder: texts.submit_form_manager_phone,
      error_helper: {
        message: texts.submit_form_phone_error,
        isError: (value) => !checkPhoneNumberSms(value),
      },
    });

    this.inputManagerSns = new LandingInput({
      name: 'manager_sns',
      type: 'url',
      placeholder: texts.submit_form_sns,
    });

    this.inputFile = new LandingInputFile({
      placeholder: texts.submit_form_file,
      name: 'file',
      helper_message: ET('mps2::landing::file::helper_message'),
    });

    this.submitButton = new ButtonAction({
      type: 'disabled',
      label: texts.submit_form_button,
      klass: klass.submit,
      size: 'large',
    });
  }

  private _isInvalidInput = (selector = 'input', checkFn: (value: string) => boolean) => {
    return $E(this.element())
      .findAll(selector)
      .find((el) => checkFn(el.getValue()));
  };

  private async _isValidInputForm() {
    const is_creator_valid = this.is_creator
      ? await this._isExistDomainName(this.inputDomain.getValue())
      : true;

    return (
      this.is_agree &&
      !this._isInvalidInput('input[required]', (value) => !value) &&
      !this._isInvalidInput("input[type='email']", (value) => !isValidEmail(value)) &&
      !this._isInvalidInput("input[type='tel']", (value) => !checkPhoneNumberSms(value)) &&
      is_creator_valid
    );
  }

  async isValidInputFormWithModal() {
    if (!this.is_agree) {
      await this._alertOpenWithFocusIn({
        title: '약관 동의 확인',
        message: html.preventEscape('신청 완료를 위해<br/>개인정보 제공을 동의해 주세요.'),
      });
      return false;
    }

    if (this._isInvalidInput('input[required]', (value) => !value)) {
      await this._alertOpenWithFocusIn({
        title: '필수사항 미입력',
        message: '필수 사항을 입력해주세요.',
      });
      return false;
    }

    if (this._isInvalidInput("input[type='email']", (value) => !isValidEmail(value))) {
      await this._alertOpenWithFocusIn({
        title: '이메일 형식 확인',
        message: html.preventEscape('이메일 형식이 잘못되었습니다.<br/>다시 입력해주세요.'),
      });
      return false;
    }

    if (this._isInvalidInput("input[type='tel']", (value) => !checkPhoneNumberSms(value))) {
      await this._alertOpenWithFocusIn({
        title: '전화번호 형식 확인',
        message: html.preventEscape('전화번호 형식을 확인해주세요.<br/>다시 입력해주세요.'),
      });
      return false;
    }

    if (this.is_creator) {
      const isExists = await this._isExistDomainName(this.inputDomain.getValue());
      if (!isExists) {
        await this._alertOpenWithFocusIn({
          title: '도메인 확인',
          message: html.preventEscape('존재하지 않는 도메인입니다.<br/>확인 후 다시 입력해 주세요.'),
        });
        return false;
      }
    }

    return true;
  }

  private _isExistDomainName = async (domain_name: string) => {
    if (!domain_name) {
      return false;
    }

    const creators = await CreatorApiS.getCreatorsByDomainNameApi({ domain_name });
    return creators.length > 0;
  };

  private _alertOpenWithFocusIn = (props: AlertProps) => {
    const activeElement = document.activeElement;
    if (activeElement instanceof HTMLElement) {
      activeElement.blur();
    }
    return Alert.open(props);
  };

  setValidationInHtml() {
    this._isValidInputForm().then((is_valid) => {
      if (is_valid) {
        this.view.setIsValid(true);
        this.submitButton.setTypeOption('blue');
      } else {
        this.view.setIsValid(false);
        this.submitButton.setTypeOption('disabled');
      }
    });
  }

  protected override onRender() {
    super.onRender();

    this.delegate('change', "input[name='marppleshop_url']", async () => {
      const is_exist = await this._isExistDomainName(this.inputDomain.getValue());
      this.inputDomain.setCustomError(!is_exist);
    });

    this.delegate('change', 'input', () => {
      this.setValidationInHtml();
    });
    this.addEventListener(InputResetEvent, () => {
      this.setValidationInHtml();
    });
  }
}
